import { cn } from '../../lib/utils';
import { Link } from 'react-router-dom';
import React, { useState, createContext, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const SidebarContext = createContext(undefined);

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}

export function SidebarProvider({ children, open: openProp, setOpen: setOpenProp, animate = true }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;
  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function Sidebar({ children, open, setOpen, animate }) {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
}

export function SidebarBody(props) {
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...props} />
    </>
  );
}

export function DesktopSidebar({ className, children, ...props }) {
  const { open, setOpen, animate } = useSidebar();
  return (
    <motion.div
      className={cn(
        'h-full px-4 py-4 hidden md:flex md:flex-col flex-shrink-0',
        className
      )}
      animate={{
        width: animate ? (open ? '240px' : '60px') : '240px',
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export function MobileSidebar({ className, children, ...props }) {
  const { open, setOpen } = useSidebar();
  return (
    <>
      <div
        className={cn(
          'h-12 px-4 flex flex-row md:hidden items-center justify-between w-full border-b border-stone-200/50 dark:border-gray-700/50',
          className
        )}
      >
        <div className="flex justify-between items-center w-full z-20">
          <div className="flex items-center space-x-2.5">
            {/* Logo placeholder — replaced by AppSidebar */}
          </div>
          <Menu
            className="text-stone-700 dark:text-gray-300 cursor-pointer w-5 h-5"
            onClick={() => setOpen(!open)}
          />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className={cn(
                'fixed h-full w-72 inset-0 bg-white dark:bg-gray-900 p-8 z-[100] flex flex-col justify-between border-r border-stone-200/50 dark:border-gray-700/50',
                className
              )}
            >
              <div
                className="absolute right-6 top-6 z-50 text-stone-700 dark:text-gray-300 cursor-pointer"
                onClick={() => setOpen(false)}
              >
                <X className="w-5 h-5" />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export function SidebarLink({ link, className, onClick }) {
  const { open, animate } = useSidebar();
  return (
    <Link
      to={link.href}
      onClick={onClick}
      className={cn(
        'flex items-center justify-start gap-2.5 group/sidebar py-2 px-2 rounded-md transition-colors hover:bg-stone-100 dark:hover:bg-gray-800',
        link.active && 'bg-stone-100 dark:bg-gray-800',
        className
      )}
    >
      {link.icon}
      <motion.span
        animate={{
          display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className={cn(
          'text-sm whitespace-pre !p-0 !m-0 transition-none',
          link.active
            ? 'text-stone-900 dark:text-white font-medium'
            : 'text-stone-600 dark:text-gray-400 group-hover/sidebar:text-stone-900 dark:group-hover/sidebar:text-white'
        )}
      >
        {link.label}
      </motion.span>
    </Link>
  );
}
